<template>
    <BaseDialog v-bind="$attrs" v-model:open="open" :title="settingsTitle" width="narrow" @close="closeDialog">
        <CalendarConnectStep
            v-if="!userDetails.has_calendar_integration"
            :source="source"
            :deeplink-source="deeplinkSource"
            :footer-target="`#${footerId}`"
            @connected="reloadPage"
            @error="showErrorToast"
            @timezone-updated="emit('timezone-updated')" />
        <template v-else>
            <label class="flex flex-col gap-2" :for="timezoneInputId">
                <span class="body-small">{{ t("components.calendar.settings.timeZone") }}</span>
                <BaseCombobox
                    v-model="selectedTimezone"
                    :input-id="timezoneInputId"
                    :clearable="false"
                    :options="calendarIntegration.timezones.map((timezone) => ({ label: timezone, value: timezone }))">
                </BaseCombobox>
            </label>
            <div class="flex flex-col gap-2">
                <div class="body-small">{{ t("components.calendar.settings.connection") }}</div>
                <CalendarIntegrationDisconnect @close="closeDialog" />
            </div>
            <div v-if="!!calendarIntegration?.private_toggle_enabled" class="flex flex-col gap-2 w-full">
                <label class="body-small flex items-center gap-2" for="private-event">
                    {{ t("components.calendar.settings.includePrivateEvents") }}
                    <BaseSwitch id="private-event" v-model="allowPrivateEvents" size="sm" />
                </label>
            </div>
        </template>
        <template #footer>
            <div v-if="!userDetails.has_calendar_integration" :id="footerId" class="contents"></div>
            <BaseButton v-else theme="primary" :disabled="!isTimezoneSelected" @click="handleSave">{{ t("common.save") }}</BaseButton>
        </template>
    </BaseDialog>
    <BaseToast v-model:open="isErrorToastOpen" theme="error">
        <template #description> <MarkdownText class="py-1" :text="errorToastMsgWithEmail" /> </template>
    </BaseToast>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { CalendarIntegration } from "~vue/types/chat_home";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, type Ref, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

import BaseToast from "../BaseToast.vue";
import CalendarConnectStep from "./CalendarConnectStep.vue";
import CalendarIntegrationDisconnect from "./CalendarIntegrationDisconnect.vue";

const { t } = useI18n();
const settingsTitle = computed(() =>
    userDetails.value?.has_calendar_integration ? t("components.calendar.settings.settingsTitle") : t("components.calendar.settings.connectionTitle"),
);

defineProps<{
    // Names the connect surface for calendar_integration_started, which the auth-url view logs.
    source: string;
    deeplinkSource?: string;
}>();
const emit = defineEmits<{ close: []; "timezone-updated": [] }>();
const open = defineModel<boolean>("open");
const userDetails = inject<Ref<UserDetails>>("user_details")!;
const calendarIntegration = inject<CalendarIntegration>("calendarIntegration")!;
const selectedTimezone = ref<{ label: string; value: string } | null>(null);
const timezoneInputId = useId();
const footerId = useId();
const allowPrivateEvents = ref(calendarIntegration?.allow_private_events ?? !calendarIntegration?.private_toggle_enabled);
const updatePrivateEventPayload = computed(() => ({
    allow_private_events: !!allowPrivateEvents.value,
}));

const isErrorToastOpen = ref(false);
const errorToastMsg = ref("");
const errorToastMsgWithEmail = computed(
    () =>
        `${t("components.calendar.settings.contactSupport", {
            message: errorToastMsg.value,
        })} [hi@valence.co](mailto:hi@valence.co)`,
);

const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { execute: updateTimezone } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

const isTimezoneSelected = computed(() => selectedTimezone.value?.value != null);

const updateUserTimezone = async () => {
    await updateTimezone();
    emit("timezone-updated");
    router.reload({ only: ["user_details"] });
};

const reloadPage = () => globalThis.location.reload();

// The connect step's polling stops when the dialog closes, so a popup OAuth that finished afterwards is caught on reopen.
const { execute: checkStatus, data: statusData } = useFetch("/api/calendar_integration/status/", { immediate: false }).get().json<{ calendarIntegrationStatus: boolean }>();
watch(open, async (isOpen) => {
    if (!isOpen || userDetails.value.has_calendar_integration) {
        return;
    }
    await checkStatus();
    // Closed while the check was in flight: reloading now could discard work elsewhere on the page.
    if (open.value && statusData.value?.calendarIntegrationStatus) {
        reloadPage();
    }
});

const showErrorToast = (message: string) => {
    errorToastMsg.value = message;
    isErrorToastOpen.value = true;
};

const updateUserPrivateEvents = async () => {
    await updatePrivateEvents();
    router.reload({ only: ["user_details"] });
};

const handleSave = async () => {
    const promises = [];

    if (isTimezoneSelected.value) {
        promises.push(updateUserTimezone());
    }

    promises.push(updateUserPrivateEvents());

    await Promise.all(promises);
    closeDialog();
};

const {
    execute: updatePrivateEvents,
    onFetchResponse: onUpdatePrivateEventsResponse,
    onFetchError: onUpdatePrivateEventsError,
    data: privateEventsToggleState,
} = useFetch("/api/calendar_integration/credential/", { immediate: false }).patch(updatePrivateEventPayload).json();

onUpdatePrivateEventsResponse(() => {
    allowPrivateEvents.value = privateEventsToggleState.value.allow_private_events;
});

onUpdatePrivateEventsError(() => {
    errorToastMsg.value = t("components.calendar.settings.privateEventsError");
    isErrorToastOpen.value = true;
    logUserInteraction("calendar_integration_error", { calendar_error_type: "private_event_toggle_error" });
});

const closeDialog = () => {
    emit("close");
};

onMounted(() => {
    if (userDetails.value.timezone) {
        selectedTimezone.value = { label: userDetails.value.timezone, value: userDetails.value.timezone };
    }
});
</script>
